import React from 'react';
import ModuleToggle from './_internal/ModuleToggle.js';
import {
  TIER_COLORS, TIER_PRESETS, MODULE_META, TIER_ORDER, defaultT,
} from './_internal/constants.js';

/**
 * <LicenseEditor> — Controlled Tier-Picker + Modul-Toggles + Limits + valid_until.
 */
export default function LicenseEditor({
  licenses,
  onChange,
  onSave,
  saving = false,
  saveMessage = '',
  disabled = false,
  tierPresets,
  moduleMeta,
  tierOrder,
  t = defaultT,
}) {
  const presets = tierPresets || TIER_PRESETS;
  const modules = moduleMeta || MODULE_META;
  const order   = tierOrder || TIER_ORDER;

  const lic = licenses || { tier: 'basic', features: {}, valid_until: '' };
  const features = lic.features || {};

  const setTier = (tier) => {
    if (disabled) return;
    const preset = presets[tier] || {};
    onChange({ ...lic, tier, features: { ...features, ...preset } });
  };

  const toggleFeature = (key, val) => {
    if (disabled) return;
    onChange({ ...lic, features: { ...features, [key]: val } });
  };

  const setLimit = (key, val) => {
    if (disabled) return;
    const n = parseInt(val, 10);
    if (!isNaN(n)) {
      onChange({ ...lic, features: { ...features, [key]: n } });
    }
  };

  const setValidUntil = (val) => {
    if (disabled) return;
    onChange({ ...lic, valid_until: val });
  };

  const isError = saveMessage && /error|fehler/i.test(saveMessage);

  return (
    <div style={{ opacity: disabled ? 0.7 : 1 }}>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="field-label">
            {t('shared.license.tier_label', 'License tier')}
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {order.map(tier => {
              const tc = TIER_COLORS[tier] || { color: '#9ca3af', bg: '#f3f4f6' };
              const active = lic.tier === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  disabled={disabled}
                  onClick={() => setTier(tier)}
                  style={{
                    padding: '5px 14px', borderRadius: 20, fontSize: 11.5, fontWeight: 700,
                    border: `2px solid ${active ? tc.color : 'var(--border)'}`,
                    background: active ? tc.bg : 'var(--bg2)',
                    color: active ? tc.color : 'var(--text3)',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s', textTransform: 'capitalize',
                  }}>
                  {t(`shared.license.tiers.${tier}`, tier)}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
            {t('shared.license.tier_hint',
               'Selecting a tier loads preset module values — individual modules remain editable afterwards.')}
          </div>
        </div>
        <div style={{ minWidth: 160 }}>
          <label className="field-label">
            {t('shared.license.valid_until', 'Valid until')}
          </label>
          <input
            className="field-input"
            type="date"
            value={lic.valid_until || ''}
            disabled={disabled}
            onChange={e => setValidUntil(e.target.value)}
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--text3)',
          textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10,
        }}>
          {t('shared.license.modules_title', 'Licensed modules')}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 8,
        }}>
          {Object.keys(modules).map(key => (
            <ModuleToggle
              key={key}
              moduleKey={key}
              value={!!features[key]}
              onChange={toggleFeature}
              disabled={disabled}
              moduleMeta={modules}
              t={t}
            />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--text3)',
          textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8,
        }}>
          {t('shared.license.limits.title', 'Limits')}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 160 }}>
            <label className="field-label">
              {t('shared.license.limits.max_users', 'Max. users')}
            </label>
            <input
              className="field-input" type="number" min={1} max={999}
              value={features.max_users ?? 10}
              disabled={disabled}
              onChange={e => setLimit('max_users', e.target.value)}
            />
          </div>
          <div style={{ minWidth: 160 }}>
            <label className="field-label">
              {t('shared.license.limits.max_audits', 'Max. audits/year')}
            </label>
            <input
              className="field-input" type="number" min={1} max={9999}
              value={features.max_audits_per_year ?? 50}
              disabled={disabled}
              onChange={e => setLimit('max_audits_per_year', e.target.value)}
            />
          </div>
          <div style={{ minWidth: 160 }}>
            <label className="field-label">
              {t('shared.license.limits.max_languages', 'Max. languages')}
            </label>
            <input
              className="field-input" type="number" min={1} max={12}
              value={features.max_languages ?? 2}
              disabled={disabled}
              onChange={e => setLimit('max_languages', e.target.value)}
            />
          </div>
        </div>
      </div>

      {onSave && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={disabled || saving}
            onClick={() => onSave()}>
            {saving
              ? t('shared.license.saving', 'Saving…')
              : t('shared.license.save_btn', 'Save licenses')}
          </button>
          {saveMessage && (
            <span style={{ fontSize: 12, color: isError ? '#ef4444' : '#10b981' }}>
              {saveMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
